import { getViewerUtcOffsetMinutes } from '@lib/date-ymd';
import { clampListQuery } from '@lib/pagination';
import { withAccessToken } from '@auth/token';
import { http, resolvedApiBaseUrl } from './http';
import type {
  ApiSuccess,
  CameraTimeline,
  PaginatedResult,
  Recording,
  RecordingListQuery,
} from './types';

export const recordingsApi = {
  async list(
    query: RecordingListQuery = {},
    signal?: AbortSignal,
  ): Promise<PaginatedResult<Recording>> {
    const res = await http.get<ApiSuccess<PaginatedResult<Recording>>>('/api/recordings', {
      params: stripUndefined(clampListQuery(query)),
      signal,
    });
    return res.data.data;
  },

  async listForCamera(
    cameraId: number,
    query: Omit<RecordingListQuery, 'cameraId'> = {},
    signal?: AbortSignal,
  ): Promise<PaginatedResult<Recording>> {
    const res = await http.get<ApiSuccess<PaginatedResult<Recording>>>(
      `/api/cameras/${cameraId}/recordings`,
      { params: stripUndefined(clampListQuery(query)), signal },
    );
    return res.data.data;
  },

  async getTimeline(
    cameraId: number,
    query: Pick<RecordingListQuery, 'date' | 'start' | 'end' | 'storageTarget' | 'utcOffsetMinutes'> = {},
    signal?: AbortSignal,
  ): Promise<CameraTimeline> {
    const params = stripUndefined({
      ...query,
      utcOffsetMinutes: query.utcOffsetMinutes ?? getViewerUtcOffsetMinutes(),
    });
    const res = await http.get<ApiSuccess<CameraTimeline>>(`/api/cameras/${cameraId}/timeline`, {
      params,
      signal,
    });
    return res.data.data;
  },

  /**
   * POST /api/recordings/export — returns an MP4 Blob (requires viewer/admin JWT when auth is enabled).
   */
  async exportClip(
    payload: {
      cameraId: number;
      start: string;
      end: string;
      storageTarget?: 'local' | 'nas';
      utcOffsetMinutes?: number;
    },
    signal?: AbortSignal,
  ): Promise<Blob> {
    const res = await http.post('/api/recordings/export', payload, {
      responseType: 'blob',
      signal,
      timeout: 610_000,
    });
    return res.data;
  },

  /**
   * GET /api/cameras/:id/snapshot — returns a JPEG Blob (requires viewer/admin JWT when auth is enabled).
   */
  async liveSnapshotJpeg(cameraId: number, signal?: AbortSignal): Promise<Blob> {
    const res = await http.get(`/api/cameras/${cameraId}/snapshot`, {
      responseType: 'blob',
      signal,
      timeout: 45_000,
    });
    return res.data;
  },

  getTimelinePlaylistUrl(
    cameraId: number,
    query: Pick<RecordingListQuery, 'date' | 'start' | 'end' | 'storageTarget' | 'utcOffsetMinutes'> = {},
  ): string {
    const params = new URLSearchParams();
    const merged = stripUndefined({
      ...query,
      utcOffsetMinutes: query.utcOffsetMinutes ?? getViewerUtcOffsetMinutes(),
    });
    for (const [key, value] of Object.entries(merged)) {
      params.set(key, String(value));
    }
    const queryString = params.toString();
    const path = `/api/cameras/${cameraId}/timeline.m3u8${queryString ? `?${queryString}` : ''}`;
    return withAccessToken(`${resolvedApiBaseUrl}${path}`);
  },
};

/** Resolves a relative recording media URL (playback or thumbnail) against `API_BASE_URL`. */
export function resolveRecordingMediaUrl(relativeUrl: string): string {
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }
  return withAccessToken(
    `${resolvedApiBaseUrl}${relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`}`,
  );
}

function stripUndefined<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== '' && v !== null) {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}
